import { useMemo } from 'react';
import { NoShowRateByType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsHooks, ChartCTA, DemoChip, InfoTipPopover } from '@frontend/analytics-shared';
import { CategoryBarChartData, Chart } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { useFetchNSROILocationsNoShowRate } from '../hooks/use-fetch-nsr-locations-no-show-rate';
import { useNSRRoiShallowStore } from '../hooks/use-nsr-roi-store';
import { CommonChartProps } from './types';

// Translation not needed as days of the week are used for sorting and matching backend keys
const weekDaysSequence = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const { colors, maxValues } = weekDaysSequence.reduce<{
  maxValues: Record<string, number>;
  colors: Record<string, string>;
}>(
  (acc, day) => {
    acc.colors[day] = theme.colors.secondary.seaweed40;
    acc.maxValues[day] = 100;
    return acc;
  },
  {
    colors: {},
    maxValues: {},
  }
);

export const NSRRoiLocationsComparisonDays = ({ isDemoAccount }: CommonChartProps) => {
  const { t } = useTranslation('analytics');
  const { filters, setFilters } = useNSRRoiShallowStore('filters', 'setFilters');
  const { locationNames } = AnalyticsHooks.useAnalyticsOrgLocations({
    module: 'ROI',
  });

  const { data, isLoading } = useFetchNSROILocationsNoShowRate({
    noShowRateType: NoShowRateByType.DAY_WISE,
    filters,
  });

  const chartData: CategoryBarChartData = useMemo(() => {
    return (
      data?.data?.locations?.reduce<CategoryBarChartData>((acc, { aggregates = [], locationId = '' }) => {
        // Create a temporary map for quick lookup of values from aggregates
        const aggregateValueMap = aggregates.reduce<Record<string, number>>((mapAcc, { name = '', value = 0 }) => {
          mapAcc[name] = value;
          return mapAcc;
        }, {});

        // Build the location's data object ensuring the order of weekDaysSequence
        const locationData: Record<string, number> = {};
        weekDaysSequence.forEach((chartKey) => {
          locationData[chartKey] = aggregateValueMap[chartKey] || 0;
        });

        acc[locationId] = locationData;
        return acc;
      }, {}) || {}
    );
  }, [data?.data?.locations]);

  const { highlight, description } = useMemo(() => {
    const locationCount = Object.keys(chartData).length;

    if (!locationCount) {
      return {
        highlight: '',
        description: '',
      };
    }

    const { sumOfMonday, sumOfTuesday, sumOfWednesday, sumOfThursday, sumOfFriday, sumOfSaturday, sumOfSunday } =
      Object.values(chartData).reduce(
        (acc, { Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday }) => {
          return {
            sumOfMonday: (acc.sumOfMonday || 0) + Math.round(Monday || 0),
            sumOfTuesday: (acc.sumOfTuesday || 0) + Math.round(Tuesday || 0),
            sumOfWednesday: (acc.sumOfWednesday || 0) + Math.round(Wednesday || 0),
            sumOfThursday: (acc.sumOfThursday || 0) + Math.round(Thursday || 0),
            sumOfFriday: (acc.sumOfFriday || 0) + Math.round(Friday || 0),
            sumOfSaturday: (acc.sumOfSaturday || 0) + Math.round(Saturday || 0),
            sumOfSunday: (acc.sumOfSunday || 0) + Math.round(Sunday || 0),
          };
        },
        {
          sumOfMonday: 0,
          sumOfTuesday: 0,
          sumOfWednesday: 0,
          sumOfThursday: 0,
          sumOfFriday: 0,
          sumOfSaturday: 0,
          sumOfSunday: 0,
        }
      );

    const avgValuesMap: Record<string, number> = {
      // Keeping naming consistent with backend keys
      Monday: sumOfMonday / locationCount,
      Tuesday: sumOfTuesday / locationCount,
      Wednesday: sumOfWednesday / locationCount,
      Thursday: sumOfThursday / locationCount,
      Friday: sumOfFriday / locationCount,
      Saturday: sumOfSaturday / locationCount,
      Sunday: sumOfSunday / locationCount,
    };

    // Sort to get highest day
    // If multiple days have highest value then collectively they will be shown in highlight
    const sortedDays = Object.entries(avgValuesMap).sort((a, b) => b[1] - a[1]);
    const highestValue = sortedDays[0][1];
    const highestDays = sortedDays.filter(([, value]) => value === highestValue).map(([day]) => day);
    const highestDay = highestDays.join(', ');

    return {
      highlight: highestDay,
      description: t('Send additional reminders for appointments scheduled on {{highlight}}', {
        highlight: highestDay,
      }),
    };
  }, [chartData, t]);

  return (
    <Chart
      colors={colors}
      isLoading={isLoading}
      labels={{
        ...locationNames,
        groupNameHeader: t('Location'),
      }}
      style={{ marginBottom: 0 }}
    >
      <Chart.Header
        title={t('No-Show Rate by Day of the Week')}
        infoTip={
          <InfoTipPopover>
            {t(
              'Track no-show rates by day of the week across locations to identify patterns and determine which days are more prone to missed appointments, enabling practices to optimize scheduling and reminders accordingly'
            )}
          </InfoTipPopover>
        }
        leftElement={isDemoAccount && <DemoChip />}
      />
      <Chart.CategoryBarChart
        appearance={{
          alternateCategoryBackgroundColor: theme.colors.neutral5,
          barSize: 32,
          formatters: formatters.appendPercent,
          labelConfig: {
            customStyles: () => ({
              dy: 4,
              fill: theme.font.colors.default,
              fontSize: theme.font.size.medium,
              fontWeight: theme.font.weight.bold,
            }),
            distanceFromBar: 16,
            placement: 'end',
            show: true,
          },
          margin: { right: 68 },
          maxValues,
          showXAxis: false,
        }}
        data={chartData}
        onClick={({ group }) => {
          setFilters({
            locationIds: [group],
          });
        }}
      />
      <ChartCTA
        buttonLabel={t('Send Reminder')}
        ctaType='sendReminders'
        title={t('Overall Highest No-Show')}
        highlight={highlight}
        description={description}
      />
    </Chart>
  );
};
